import { useCallback, useEffect } from 'react';
import { PrimitiveAtom, SetStateAction, WritableAtom, useAtomValue, useSetAtom } from 'jotai';
import { CameraTargetProps, CurrentImageProps, Number3 } from '@fabric-web/3d-viewer';
import queryString from 'query-string';
import { updateUrl } from '@/utils';
import { parseFloatParameter, updateCameraTargetUrlParameters } from './utils';

export type CameraTargetDeeplinkState = {
  shouldUpdateUrl: boolean;
  cameraTargetAtom: PrimitiveAtom<CameraTargetProps | undefined>;
  selectedSphericalAtom: WritableAtom<
    CurrentImageProps | undefined,
    [SetStateAction<CurrentImageProps | undefined>],
    void
  >;
  locations: { id: string; name: string }[] | undefined;
  mapLocationToCurrentImage: (
    location: any,
    lookAt?: { x: number; y: number; z: number },
    fov?: number
  ) => CurrentImageProps | undefined;
  onSphericalSelected?: (image: CurrentImageProps) => void;
  onCameraTargetSet?: (target: CameraTargetProps) => void;
};

export const useCameraTargetDeeplink = (state: CameraTargetDeeplinkState) => {
  const {
    shouldUpdateUrl,
    cameraTargetAtom,
    selectedSphericalAtom,
    locations,
    mapLocationToCurrentImage,
    onSphericalSelected,
    onCameraTargetSet,
  } = state;

  const cameraTarget = useAtomValue(cameraTargetAtom);
  const selectedSpherical = useAtomValue(selectedSphericalAtom);

  const setCameraTarget = useSetAtom(cameraTargetAtom);
  const setSelectedSpherical = useSetAtom(selectedSphericalAtom);

  useEffect(() => {
    if (shouldUpdateUrl && cameraTarget) {
      updateCameraTargetUrlParameters(cameraTarget);
    }
  }, [shouldUpdateUrl, cameraTarget]);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl('selectedSpherical', selectedSpherical?.name);
    }
  }, [shouldUpdateUrl, selectedSpherical]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      if (parsed.selectedSpherical && typeof parsed.selectedSpherical === 'string' && locations) {
        const matched = locations.find((location) => location.name === parsed.selectedSpherical);
        const fov = parsed.fov ? parseFloatParameter(parsed.fov) : undefined;
        const lookAt =
          parsed['look.x'] && parsed['look.y'] && parsed['look.z']
            ? {
                x: parseFloatParameter(parsed['look.x']),
                y: parseFloatParameter(parsed['look.y']),
                z: parseFloatParameter(parsed['look.z']),
              }
            : undefined;
        const image = mapLocationToCurrentImage(matched, lookAt, fov);
        if (image) {
          setSelectedSpherical(image);
          onSphericalSelected?.(image);
        }
        return;
      }

      if (
        parsed['look.x'] &&
        parsed['look.y'] &&
        parsed['look.z'] &&
        parsed['position.x'] &&
        parsed['position.y'] &&
        parsed['position.z']
      ) {
        const fov = parsed.fov ? parseFloatParameter(parsed.fov) : 0;
        const lookAt: Number3 = [
          parseFloatParameter(parsed['look.x']),
          parseFloatParameter(parsed['look.y']),
          parseFloatParameter(parsed['look.z']),
        ];
        const position: Number3 = [
          parseFloatParameter(parsed['position.x']),
          parseFloatParameter(parsed['position.y']),
          parseFloatParameter(parsed['position.z']),
        ];
        const target: CameraTargetProps = {
          position,
          lookAt,
          fov: fov > 0 ? fov : undefined,
        };
        setCameraTarget(target);
        onCameraTargetSet?.(target);
      }
    },
    [
      locations,
      mapLocationToCurrentImage,
      setSelectedSpherical,
      onSphericalSelected,
      setCameraTarget,
      onCameraTargetSet,
    ]
  );

  return { applyFromParsed };
};
